import { useEffect, useMemo, useState } from 'react';
import type { AuthArgs } from '../api/authArgs';
import { getCustomerContext, type CustomerContext, type CustomerSubscription } from '../api/customerClient';
import { CASE_HISTORY_LIMIT, CaseHistoryError, getCaseHistory, type CaseHistoryFilter, type SupportCase } from '../api/caseClient';
import type { UserInfo } from '../api/userClient';

/**
 * The "Peridot Tabbed Chat experience": a vertical rail on the left of the signed-in chat window
 * that switches the main pane between the live conversation and read-only context panels
 * (My Info, Subscription, History, Product Profile, Device Information).
 *
 * The rail + panels are only ever shown to signed-in users — anonymous users keep the plain chat.
 * Subscription/Product data comes from the Shopper OBO read (GET /api/v1/customer/context);
 * My Info comes from the already-fetched {@link UserInfo}; Device Information is derived client-side.
 * History reads MSaaS cases through the API's delegated MCP endpoint.
 */
export type ChatTab = 'chat' | 'myinfo' | 'subscription' | 'history' | 'product' | 'device';

/** Non-chat tabs that render a context panel. Order matches the rail top-to-bottom. */
export const CHAT_INFO_TABS: ChatTab[] = ['myinfo', 'subscription', 'history', 'product', 'device'];

// Public Microsoft account surfaces linked from the panels.
const AMC_URL = 'https://account.microsoft.com';
const SUBSCRIPTIONS_URL = 'https://account.microsoft.com/services';
const DEVICES_URL = 'https://account.microsoft.com/devices';

interface TabMeta {
  key: ChatTab;
  label: string;
  icon: (props: { size?: number; color?: string }) => React.ReactElement;
}

// --- Rail icons (simple, single-path Fluent-style glyphs) ---
function ChatIcon({ size = 20, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.9-.9L3 21l1.9-5.6A8.5 8.5 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z" />
    </svg>
  );
}
function PersonIcon({ size = 20, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
      <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2c-4.42 0-8 2.24-8 5v1a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-1c0-2.76-3.58-5-8-5z" />
    </svg>
  );
}
function SubscriptionIcon({ size = 20, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="M2.5 9.5h19" />
    </svg>
  );
}
function HistoryIcon({ size = 20, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
      <path d="M3 4v4h4" />
      <path d="M12 8v4l3 2" />
    </svg>
  );
}
function ProductIcon({ size = 20, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <path d="M3.3 7 12 12l8.7-5M12 22V12" />
    </svg>
  );
}
function DeviceIcon({ size = 20, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.5" y="4" width="19" height="12" rx="1.5" />
      <path d="M8 20h8M12 16v4" />
    </svg>
  );
}

const TAB_META: Record<ChatTab, TabMeta> = {
  chat: { key: 'chat', label: 'Chat', icon: ChatIcon },
  myinfo: { key: 'myinfo', label: 'My Info', icon: PersonIcon },
  subscription: { key: 'subscription', label: 'Subscriptions', icon: SubscriptionIcon },
  history: { key: 'history', label: 'History', icon: HistoryIcon },
  product: { key: 'product', label: 'Product Profile', icon: ProductIcon },
  device: { key: 'device', label: 'Device Information', icon: DeviceIcon },
};

const RAIL_ORDER: ChatTab[] = ['chat', 'myinfo', 'subscription', 'history', 'product', 'device'];

/** The vertical icon rail shown on the left of the signed-in chat window. */
export function ChatTabRail({ active, onSelect }: { active: ChatTab; onSelect: (tab: ChatTab) => void }) {
  return (
    <nav style={styles.rail} aria-label="Chat sections">
      {RAIL_ORDER.map(key => {
        const meta = TAB_META[key];
        const isActive = key === active;
        const Icon = meta.icon;
        return (
          <button
            key={key}
            type="button"
            className="hover-rail-button"
            onClick={() => onSelect(key)}
            title={meta.label}
            aria-label={meta.label}
            aria-current={isActive ? 'page' : undefined}
            style={{ ...styles.railButton, ...(isActive ? styles.railButtonActive : null) }}
          >
            <Icon size={20} color={isActive ? '#0078d4' : '#4b5563'} />
          </button>
        );
      })}
    </nav>
  );
}

/** Renders the context panel for the selected (non-chat) tab. */
export function ChatInfoPanel({
  tab,
  userInfo,
  getAuthArgs,
}: {
  tab: ChatTab;
  userInfo: UserInfo | null;
  getAuthArgs: () => Promise<AuthArgs | null>;
}) {
  return (
    <div style={styles.panel}>
      <div style={styles.panelHeader}>{TAB_META[tab].label}</div>
      <div style={styles.panelBody}>
        {tab === 'myinfo' && <MyInfoPanel userInfo={userInfo} />}
        {tab === 'subscription' && <SubscriptionPanel getAuthArgs={getAuthArgs} />}
        {tab === 'history' && <HistoryPanel getAuthArgs={getAuthArgs} />}
        {tab === 'product' && <ProductProfilePanel getAuthArgs={getAuthArgs} />}
        {tab === 'device' && <DevicePanel />}
      </div>
    </div>
  );
}

// --- My Info ---
function MyInfoPanel({ userInfo }: { userInfo: UserInfo | null }) {
  if (!userInfo) {
    return <ComingSoon message="Loading your profile…" />;
  }
  const name = userInfo.displayName || [userInfo.givenName, userInfo.surname].filter(Boolean).join(' ') || '—';
  const providerLabel = userInfo.provider === 'WindowsLiveId'
    ? 'Personal Microsoft account'
    : userInfo.provider === 'OrgId' ? 'Work or school account' : (userInfo.provider ?? '—');
  return (
    <>
      <Field label="Signed in as" value={name} />
      <Field label="Email" value={userInfo.email ?? '—'} />
      <Field label="Account type" value={providerLabel} />
      {userInfo.userPuid && <Field label="PUID" value={userInfo.userPuid} mono />}
      <a href={AMC_URL} target="_blank" rel="noreferrer" style={styles.link}>
        Manage your Microsoft account (AMC) →
      </a>
    </>
  );
}

// --- Subscription information ---
function SubscriptionPanel({ getAuthArgs }: { getAuthArgs: () => Promise<AuthArgs | null> }) {
  const { context, loaded, error } = useCustomerContext(getAuthArgs);
  const subs = context?.subscriptions ?? [];
  return (
    <>
      {!loaded && <ComingSoon message="Loading your subscriptions…" />}
      {loaded && error && <ComingSoon message="We couldn't load your subscriptions right now." />}
      {loaded && !error && subs.length === 0 && <ComingSoon message="No active subscriptions found." />}
      {loaded && !error && subs.length > 0 && (
        <div style={styles.cardList}>
          {subs.map((s, i) => <SubscriptionCard key={i} sub={s} />)}
        </div>
      )}
      {loaded && !error && (
        <a href={SUBSCRIPTIONS_URL} target="_blank" rel="noreferrer" style={styles.link}>
          Manage subscriptions →
        </a>
      )}
    </>
  );
}

function SubscriptionCard({ sub }: { sub: CustomerSubscription }) {
  const title = sub.skuTitle || sub.productName || 'Subscription';
  const active = (sub.state ?? '').toLowerCase() === 'active';
  return (
    <div style={styles.card}>
      <div style={styles.cardTitleRow}>
        <span style={styles.cardTitle}>{title}</span>
        <StatePill state={sub.state} active={active} />
      </div>
      <div style={styles.cardMetaRow}>
        {sub.isTrial && <span style={styles.tag}>Trial</span>}
        <span style={styles.cardMeta}>
          {sub.autoRenewEnabled ? 'Auto-renew on' : 'Auto-renew off'}
        </span>
        {sub.market && <span style={styles.cardMeta}>· {sub.market}</span>}
      </div>
      {sub.renewalDateUtc && (
        <div style={styles.cardMeta}>
          {sub.autoRenewEnabled ? 'Renews' : 'Expires'} {formatDate(sub.renewalDateUtc)}
        </div>
      )}
    </div>
  );
}

// --- Support case history ---
function HistoryPanel({ getAuthArgs }: { getAuthArgs: () => Promise<AuthArgs | null> }) {
  const [filter, setFilter] = useState<CaseHistoryFilter>('active');
  const [refresh, setRefresh] = useState(0);
  return (
    <>
      <div style={styles.cardTitleRow}>
        <label style={styles.cardMetaRow}>
          Cases
          <select
            value={filter}
            onChange={event => setFilter(event.target.value === 'active' ? 'active' : 'inactive')}
            style={styles.historyControl}
          >
            <option value="active">Active</option>
            <option value="inactive">Not active</option>
          </select>
        </label>
        <button type="button" onClick={() => setRefresh(value => value + 1)} style={styles.historyControl}>
          Refresh
        </button>
      </div>
      <div style={styles.cardMeta}>Up to {CASE_HISTORY_LIMIT} matching cases, newest created first.</div>
      <CaseHistoryResults key={`${filter}:${refresh}`} filter={filter} getAuthArgs={getAuthArgs} />
    </>
  );
}

type CaseHistoryState =
  | { status: 'loading' }
  | { status: 'loaded'; cases: SupportCase[] }
  | { status: 'error'; message: string };

function CaseHistoryResults({
  filter,
  getAuthArgs,
}: {
  filter: CaseHistoryFilter;
  getAuthArgs: () => Promise<AuthArgs | null>;
}) {
  const [result, setResult] = useState<CaseHistoryState>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    setResult({ status: 'loading' });
    void (async () => {
      try {
        const auth = await getAuthArgs();
        if (controller.signal.aborted) return;
        if (!auth) throw new CaseHistoryError(401);
        const cases = await getCaseHistory(auth, filter, controller.signal);
        if (!controller.signal.aborted) setResult({ status: 'loaded', cases });
      } catch (error: unknown) {
        if (controller.signal.aborted) return;
        const status = error instanceof CaseHistoryError ? error.status : 0;
        const message = status === 401 || status === 403
          ? 'Sign in again to load your support cases.'
          : "We couldn't load your support cases. Select Refresh to try again.";
        setResult({ status: 'error', message });
      }
    })();
    return () => controller.abort();
  }, [filter, getAuthArgs]);

  if (result.status === 'loading') {
    return <div role="status" style={styles.empty}>Loading your support cases...</div>;
  }
  if (result.status === 'error') {
    return <div role="alert" style={styles.empty}>{result.message}</div>;
  }
  if (result.cases.length === 0) {
    return <div role="status" style={styles.empty}>No {filter === 'active' ? 'active' : 'non-active'} support cases found.</div>;
  }
  return (
    <div style={styles.cardList}>
      {result.cases.map((supportCase, index) => (
        <article key={`${supportCase.caseNumber}:${index}`} style={styles.card}>
          <div style={styles.cardTitleRow}>
            <span style={styles.cardTitle}>{supportCase.title || 'Support case'}</span>
            <StatePill state={supportCase.state} active={supportCase.state?.toLowerCase() === 'active'} />
          </div>
          <div style={styles.cardMeta}>Case <span style={styles.mono}>{supportCase.caseNumber}</span></div>
          {supportCase.stateAnnotation && (
            <div style={styles.cardMeta}>Status: {supportCase.stateAnnotation}</div>
          )}
          {supportCase.issueContext && (
            <details>
              <summary style={styles.historySummary}>Issue context</summary>
              <div style={styles.issueContext}>{supportCase.issueContext}</div>
            </details>
          )}
        </article>
      ))}
    </div>
  );
}

// --- Product Profile ---
function ProductProfilePanel({ getAuthArgs }: { getAuthArgs: () => Promise<AuthArgs | null> }) {
  const { context, loaded, error } = useCustomerContext(getAuthArgs);
  const products = useMemo(() => dedupeProducts(context?.subscriptions ?? []), [context]);
  return (
    <>
      <div style={styles.sectionLabel}>Your products</div>
      {!loaded && <ComingSoon message="Loading your products…" />}
      {loaded && error && <ComingSoon message="We couldn't load your products right now." />}
      {loaded && !error && products.length === 0 && <ComingSoon message="No products found." />}
      {loaded && !error && products.length > 0 && (
        <ul style={styles.plainList}>
          {products.map((p, i) => (
            <li key={i} style={styles.plainListItem}>
              <span style={styles.dot(p.active)} />
              <div>
                <div style={styles.cardTitle}>{p.name}</div>
                {p.family && <div style={styles.cardMeta}>{p.family}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
      <div style={{ ...styles.sectionLabel, marginTop: 16 }}>Owned Microsoft devices</div>
      <ComingSoon message="Your registered Microsoft devices will appear here." />
      <a href={DEVICES_URL} target="_blank" rel="noreferrer" style={styles.link}>
        Manage your devices →
      </a>
    </>
  );
}

// --- Device Information (client-side) ---
function DevicePanel() {
  const info = useMemo(() => readDeviceInfo(), []);
  // The legacy UA string freezes at "Windows NT 10.0" for both Windows 10 and 11,
  // so readDeviceInfo can only report "Windows 10/11". When the browser supports
  // User-Agent Client Hints (Edge/Chrome), refine it to the exact version.
  const [os, setOs] = useState(info.os);
  useEffect(() => {
    let cancelled = false;
    void detectOsPrecise(info.os).then(precise => {
      if (!cancelled) setOs(precise);
    });
    return () => { cancelled = true; };
  }, [info.os]);
  return (
    <>
      <Field label="Operating system" value={os} />
      <Field label="Browser" value={info.browser} />
      <Field label="Language" value={info.language} />
      <Field label="Screen" value={info.screen} />
      <Field label="Timezone" value={info.timezone} />
      <Field label="User agent" value={info.userAgent} mono />
    </>
  );
}

// --- Shared bits ---
function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={styles.field}>
      <div style={styles.fieldLabel}>{label}</div>
      <div style={{ ...styles.fieldValue, ...(mono ? styles.mono : null) }}>{value}</div>
    </div>
  );
}

function StatePill({ state, active }: { state?: string | null; active: boolean }) {
  return (
    <span style={{ ...styles.pill, ...(active ? styles.pillActive : styles.pillInactive) }}>
      {state || 'Unknown'}
    </span>
  );
}

function ComingSoon({ message }: { message: string }) {
  return <div style={styles.empty}>{message}</div>;
}

/** Fetches the Shopper customer context once per mount; shared by Subscription + Product panels. */
function useCustomerContext(getAuthArgs: () => Promise<AuthArgs | null>) {
  const [context, setContext] = useState<CustomerContext | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError(false);
    void (async () => {
      try {
        const auth = await getAuthArgs();
        if (!auth) throw new Error('No auth');
        const ctx = await getCustomerContext(auth);
        if (!cancelled) setContext(ctx);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthArgs]);

  return { context, loaded, error };
}

interface ProductRow { name: string; family?: string | null; active: boolean }

function dedupeProducts(subs: CustomerSubscription[]): ProductRow[] {
  const seen = new Map<string, ProductRow>();
  for (const s of subs) {
    const name = s.skuTitle || s.productName || 'Subscription';
    if (seen.has(name)) continue;
    seen.set(name, { name, family: s.productFamily, active: (s.state ?? '').toLowerCase() === 'active' });
  }
  return [...seen.values()];
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

interface DeviceInfo { os: string; browser: string; language: string; screen: string; timezone: string; userAgent: string }

function readDeviceInfo(): DeviceInfo {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const language = typeof navigator !== 'undefined' ? navigator.language : '—';
  const os = detectOs(ua);
  const browser = detectBrowser(ua);
  const screen = typeof window !== 'undefined' && window.screen
    ? `${window.screen.width}×${window.screen.height} @ ${window.devicePixelRatio || 1}x`
    : '—';
  let timezone = '—';
  try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '—'; } catch { /* ignore */ }
  return { os, browser, language: language || '—', screen, timezone, userAgent: ua || '—' };
}

function detectOs(ua: string): string {
  if (/Windows NT 10/.test(ua)) return 'Windows 10/11';
  if (/Windows NT/.test(ua)) return 'Windows';
  if (/Mac OS X/.test(ua)) return 'macOS';
  if (/Android/.test(ua)) return 'Android';
  if (/(iPhone|iPad|iPod)/.test(ua)) return 'iOS';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown';
}

// Minimal typing for the User-Agent Client Hints API (not in lib.dom yet).
interface UADataValues {
  platform?: string;
  platformVersion?: string;
}
interface NavigatorUAData {
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<UADataValues>;
}

function getUaData(): NavigatorUAData | undefined {
  if (typeof navigator === 'undefined') return undefined;
  return (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData;
}

// Resolves the exact OS when possible. On Windows the legacy UA string can't
// distinguish 10 from 11, so we use the high-entropy platformVersion:
// major >= 13 → Windows 11, 1..12 → Windows 10, 0 → Windows 7/8.x.
// Falls back to the UA-derived value for non-Chromium browsers or on any error.
async function detectOsPrecise(fallback: string): Promise<string> {
  const uaData = getUaData();
  if (!uaData?.getHighEntropyValues) return fallback;
  try {
    const values = await uaData.getHighEntropyValues(['platformVersion']);
    const platform = values.platform ?? uaData.platform;
    if (platform === 'Windows' && values.platformVersion) {
      const major = parseInt(values.platformVersion.split('.')[0], 10);
      if (Number.isFinite(major)) {
        if (major === 0) return 'Windows 7/8';
        return major >= 13 ? 'Windows 11' : 'Windows 10';
      }
    }
  } catch {
    /* ignore — fall back to the UA-derived value */
  }
  return fallback;
}

function detectBrowser(ua: string): string {
  if (/Edg\//.test(ua)) return 'Microsoft Edge';
  if (/OPR\//.test(ua)) return 'Opera';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Safari\//.test(ua)) return 'Safari';
  return 'Unknown';
}

const styles = {
  rail: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    padding: '10px 6px',
    background: '#eef2f7',
    borderRight: '1px solid #e5e7eb',
    flex: '0 0 auto',
  } as React.CSSProperties,
  railButton: {
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'transparent',
    borderRadius: 10,
    cursor: 'pointer',
    color: '#4b5563',
  } as React.CSSProperties,
  railButtonActive: {
    background: '#ffffff',
    borderColor: '#cfe0f4',
    boxShadow: '0 1px 3px rgba(15,23,42,0.12)',
  } as React.CSSProperties,
  panel: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    background: '#f8fafc',
    overflow: 'hidden',
  } as React.CSSProperties,
  panelHeader: {
    padding: '14px 16px',
    fontWeight: 650,
    fontSize: 14,
    color: '#111827',
    borderBottom: '1px solid #e5e7eb',
    background: '#ffffff',
  } as React.CSSProperties,
  panelBody: {
    flex: 1,
    overflowY: 'auto',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  } as React.CSSProperties,
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: '8px 12px',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
  } as React.CSSProperties,
  fieldLabel: { fontSize: 11, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 } as React.CSSProperties,
  fieldValue: { fontSize: 13, color: '#111827', wordBreak: 'break-word' } as React.CSSProperties,
  mono: { fontFamily: 'Consolas, ui-monospace, monospace', fontSize: 12 } as React.CSSProperties,
  link: {
    marginTop: 4,
    fontSize: 13,
    color: '#0078d4',
    textDecoration: 'none',
    fontWeight: 600,
  } as React.CSSProperties,
  sectionLabel: { fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.4 } as React.CSSProperties,
  cardList: { display: 'flex', flexDirection: 'column', gap: 10 } as React.CSSProperties,
  card: {
    padding: 12,
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  } as React.CSSProperties,
  cardTitleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 } as React.CSSProperties,
  cardTitle: { fontSize: 13, fontWeight: 650, color: '#111827' } as React.CSSProperties,
  historyControl: { font: 'inherit', fontSize: 12, padding: '6px 8px', background: '#ffffff', color: '#111827', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' } as React.CSSProperties,
  historySummary: { fontSize: 12, color: '#0078d4', cursor: 'pointer' } as React.CSSProperties,
  issueContext: { fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', paddingTop: 8 } as React.CSSProperties,
  cardMetaRow: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' } as React.CSSProperties,
  cardMeta: { fontSize: 12, color: '#6b7280' } as React.CSSProperties,
  tag: {
    fontSize: 10,
    fontWeight: 700,
    color: '#92400e',
    background: '#fef3c7',
    borderRadius: 6,
    padding: '1px 6px',
    textTransform: 'uppercase',
  } as React.CSSProperties,
  pill: { fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '2px 8px' } as React.CSSProperties,
  pillActive: { color: '#065f46', background: '#d1fae5' } as React.CSSProperties,
  pillInactive: { color: '#6b7280', background: '#f3f4f6' } as React.CSSProperties,
  plainList: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 } as React.CSSProperties,
  plainListItem: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 10 } as React.CSSProperties,
  dot: (active: boolean): React.CSSProperties => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginTop: 5,
    flex: '0 0 auto',
    background: active ? '#10b981' : '#9ca3af',
  }),
  empty: {
    padding: '16px 12px',
    fontSize: 13,
    color: '#6b7280',
    textAlign: 'center',
    background: '#ffffff',
    border: '1px dashed #d1d5db',
    borderRadius: 10,
  } as React.CSSProperties,
} as const;
